import { Grid, Image } from "antd";
import { useEffect, useState } from "react";
import { useGetAssetDownloadUrl } from "@/common/artemis-operations/assets";
import {
	ASSET_DOWNLOAD_ERROR_DESC,
	ASSET_DOWNLOAD_ERROR_TITLE,
} from "@/common/messages";
import type { Asset } from "@/common/model";
import { QueryGuard } from "../base";
import {
	DocumentEditor,
	type DocumentViewMode,
	DocumentViewSelector,
} from "../document-editor";
import { CustomDrawer } from "../layout/workspace/custom-drawer";

interface AssetViewerDrawerProps {
	asset: Asset | null;
	onClose: () => void;
}

export function AssetViewerDrawer({ asset, onClose }: AssetViewerDrawerProps) {
	const [content, setContent] = useState<string | null>(null);
	const [error, setError] = useState<boolean>(false);
	const [viewMode, setViewMode] = useState<DocumentViewMode>("preview");
	const { mutateAsync: getDownloadUrl } = useGetAssetDownloadUrl();
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;
	const isImage = asset?.mediaType?.startsWith("image/");

	useEffect(() => {
		if (!asset) return;
		setContent(null);
		setError(false);
		getDownloadUrl(asset.id)
			// for images the presigned URL itself is the content (used as <img> src)
			.then((url) => (isImage ? url : fetch(url).then((res) => res.text())))
			.then(setContent)
			.catch(() => setError(true));
	}, [asset?.id, getDownloadUrl, asset, isImage]);

	const isLoading = !!asset && content === null && !error;

	return (
		<CustomDrawer
			title={asset?.name}
			open={!!asset}
			onClose={onClose}
			size="large"
			styles={{ wrapper: { minHeight: isMobile ? "90%" : undefined } }}
			extra={
				!isLoading && !error && !isImage ? (
					<DocumentViewSelector
						initialViewMode={viewMode}
						onViewModeChange={setViewMode}
						enabledViewModes={["preview", "raw"]}
					/>
				) : null
			}
		>
			<QueryGuard
				isPending={isLoading}
				isError={error}
				title={ASSET_DOWNLOAD_ERROR_TITLE}
				message={ASSET_DOWNLOAD_ERROR_DESC}
			>
				{content !== null &&
					(isImage ? (
						<Image
							src={content}
							alt={asset?.name}
							style={{ maxWidth: "100%" }}
						/>
					) : (
						<DocumentEditor content={content} viewMode={viewMode} readOnly />
					))}
			</QueryGuard>
		</CustomDrawer>
	);
}
